import * as THREE from 'three';
import { GroundUtils } from '@/physics/groundPhysics';

export type AimKeys = { left: boolean, right: boolean, forward: boolean, backward: boolean };

type ShotPerspectiveCameraOptions = {
  fov?: number,
  near?: number,
  far?: number,
  aimSpeed?: number;
  autoPosition?: boolean;
  trackingDelay?: number;
  cameraOffsetX?: number;
  cameraOffsetYZ?: [number, number];
  cameraTrackingOffsetYZ?: [number, number];
  canvas?: HTMLElement | null;
  scene?: THREE.Object3D | THREE.Object3D[];
}

export class ShotPerspectiveCamera extends THREE.PerspectiveCamera {
  scene?: THREE.Object3D | THREE.Object3D[];
  canvas?: HTMLElement | null;
  shotDirection: THREE.Vector3;
  staticCamPos: THREE.Vector3;
  staticLookAt: THREE.Vector3;
  currentLookAt: THREE.Vector3;
  desiredCamPos: THREE.Vector3;
  desiredLookAt: THREE.Vector3;
  originalFov: number;
  autoPosition: boolean;
  cameraOffsetX: number;
  cameraOffsetYZ: [number, number];
  cameraTrackingOffsetYZ: [number, number];
  cameraTrackingFov: number;
  isTracking: boolean;
  // isAiming: boolean;
  aimVelocity: { lateral: number, longitudinal: number };
  aimSpeed: number;
  aimKeys: AimKeys;
  trackingDelay: number;
  
  #lastGroundCheck: THREE.Vector3;
  #groundY: number;
  #right: THREE.Vector3;
  #up: THREE.Vector3;
  #trackTimeout: number;
  #activeFrustumOffset: number = 0;
  #tmpBack: THREE.Vector3;
  #tmpRight: THREE.Vector3;


  constructor(
    options: ShotPerspectiveCameraOptions = {}
  ) {
    const aspect = (window.innerWidth / window.innerHeight);
    const fov = options.fov ?? 20;
    const near = options.near ?? 2;
    const far = options.far ?? 500;
    super(fov, aspect, near, far);
    this.originalFov = fov;
    this.scene = options.scene;
    this.canvas = options.canvas;

    // defaults
    this.cameraOffsetX = options.cameraOffsetX ?? 0;
    this.cameraOffsetYZ = options.cameraOffsetYZ ?? [2.5, 15];
    this.cameraTrackingOffsetYZ = options.cameraTrackingOffsetYZ ?? [6, 25];
    this.cameraTrackingFov = 35;
    this.autoPosition = options.autoPosition ?? false;

    this.#activeFrustumOffset = this.cameraOffsetX;
    this.projectionMatrix.elements[8] = this.#activeFrustumOffset;
    this.shotDirection = new THREE.Vector3();
    this.staticCamPos = new THREE.Vector3();
    this.staticLookAt = new THREE.Vector3();
    this.currentLookAt = new THREE.Vector3();
    this.desiredCamPos = new THREE.Vector3();
    this.desiredLookAt = new THREE.Vector3();
    
    this.isTracking = false;
    this.layers.enable(2);
    
    this.aimVelocity = { lateral: 0, longitudinal: 0 };
    this.#lastGroundCheck = new THREE.Vector3();
    this.#groundY = 0;
    this.#right = new THREE.Vector3();
    this.#up = new THREE.Vector3(0, 1, 0);
    // Initialize in constructor
    this.#tmpBack = new THREE.Vector3();
    this.#tmpRight = new THREE.Vector3();

    this.aimSpeed = options.aimSpeed ?? 7; // meters per second
    this.aimKeys = { left: false, right: false, forward: false, backward: false };    
    this.trackingDelay = options.trackingDelay ?? 3000;
    this.#trackTimeout = 0;

    if (this.canvas) {
      const resizeObserver = new ResizeObserver((_entries) => this._handleResize());
      resizeObserver.observe(this.canvas);
    } else {
      window.addEventListener('resize', this._handleResize.bind(this));
    }
    requestAnimationFrame(() => this._handleResize());
  }

  setScene(scene?: THREE.Object3D | THREE.Object3D[]) {
    this.scene = scene;
  }

  _handleResize() {
    let width = window.innerWidth;
    let height = window.innerHeight;
    if (this.canvas instanceof HTMLCanvasElement) {
      width = this.canvas.width;
      height = this.canvas.height;
    }
    this.aspect = width / height;
    this.updateProjectionMatrix();
    this.projectionMatrix.elements[8] = this.#activeFrustumOffset;
  }
  
  applyFrustumOffset(dt: number, target: number, smooth: boolean) {
    if (smooth) {
      const t = 1 - Math.exp(-Math.min(dt, 1 / 60) * 3);
      this.#activeFrustumOffset += (target - this.#activeFrustumOffset) * t;
    } else {
      if (this.#activeFrustumOffset === target) return;
      this.#activeFrustumOffset = target;
    }
    this.projectionMatrix.elements[8] = this.#activeFrustumOffset;
  }

  setTracking(track: boolean, timeScale = 1) {
    clearTimeout(this.#trackTimeout);
    if (track) {
      const trackingDelay = Math.max(this.trackingDelay * timeScale, 500);
      this.#trackTimeout = setTimeout(() => {
        this.isTracking = true;
      }, trackingDelay);
    } else {
      this.isTracking = false;
    }
  }
  
  setPositions(startPoint: THREE.Vector3, aimPoint: THREE.Vector3) {
    const back = this.#tmpBack.subVectors(startPoint, aimPoint).normalize();
    back.y = 0;
    back.normalize();

    const minDistance = 50;
    const maxDistance = 100;
    const minFov = this.originalFov - 5;
    const maxFov = this.originalFov;

    const dist = startPoint.distanceTo(aimPoint);
    const clampedDistance = Math.max(minDistance, Math.min(dist, maxDistance));
    const t = (clampedDistance - minDistance) / (maxDistance - minDistance);
    const targetFov = THREE.MathUtils.lerp(minFov, maxFov, t);

    this.fov = targetFov;
    this.updateProjectionMatrix();
    this.projectionMatrix.elements[8] = this.#activeFrustumOffset;
    
    
    let finalY = this.cameraOffsetYZ[0];
    let finalZ = this.cameraOffsetYZ[1];

    if (this.autoPosition) {
      // --- Binary search for zOffset that places ball at bottom of screen ---
      // finalY = this.cameraOffsetYZ[0];
      // NDC y: -1 = bottom pixel, +1 = top pixel
      // -0.85 = ball sits ~7.5% up from bottom edge
      const targetNdcY = -0.85;
  
      let lo = 3, hi = 40;
      for (let i = 0; i < 12; i++) {
        const mid = (lo + hi) / 2;
        this.position.copy(startPoint).addScaledVector(back, mid);
        this.position.y += finalY;
        this.lookAt(aimPoint);
        this.updateMatrixWorld(true);
  
        const ndc = startPoint.clone().project(this);
  
        if (ndc.y > targetNdcY) {
          hi = mid;  // ball too high → bring camera closer
        } else {
          lo = mid;  // ball too low → push camera back
        }
  
      }
  
      const zOffset = (lo + hi) / 2;
      const minZ = 12; // don't crowd the ball on short shots
      finalZ = Math.max(zOffset, minZ);
    }

    // this.staticCamPos.copy(startPoint).addScaledVector(back, zOffset);
    this.staticCamPos.copy(startPoint).addScaledVector(back, finalZ);

    this.staticCamPos.y += finalY;
    this.staticLookAt.copy(aimPoint);

    this.shotDirection.subVectors(aimPoint, startPoint);
    this.shotDirection.y = 0;
    this.shotDirection.normalize();
  }
  

  updateAim(dt: number, startPoint: THREE.Vector3, aimPoint: THREE.Vector3) {
    const { left, right, forward, backward } = this.aimKeys;
    const ramp = 1 - Math.exp(-dt * 20);
    const decay = 1 - Math.exp(-dt * 12);

    const latTarget = left ? 1 : right ? -1 : 0;
    const lonTarget = forward ? 1 : backward ? -1 : 0;

    this.aimVelocity.lateral += (latTarget - this.aimVelocity.lateral) * (latTarget ? ramp : decay);
    this.aimVelocity.longitudinal += (lonTarget - this.aimVelocity.longitudinal) * (lonTarget ? ramp : decay);

    if (Math.abs(this.aimVelocity.lateral) < 0.001) this.aimVelocity.lateral = 0;
    if (Math.abs(this.aimVelocity.longitudinal) < 0.001) this.aimVelocity.longitudinal = 0;

    if (this.aimVelocity.lateral === 0 && this.aimVelocity.longitudinal === 0) {
      return
    }

    const dist = startPoint.distanceTo(aimPoint);
    const angleStep = this.aimSpeed * dt * (Math.PI / 180); // degrees per second

    if (this.aimVelocity.lateral !== 0) {
      const angle = this.aimVelocity.lateral * angleStep;
      const offset = this.#tmpBack.subVectors(aimPoint, startPoint);
      offset.applyAxisAngle(this.#up, angle);
      aimPoint.copy(startPoint).add(offset);
    }

    if (this.aimVelocity.longitudinal !== 0) {
      aimPoint.addScaledVector(this.shotDirection, this.aimVelocity.longitudinal * dist * angleStep);
    }

    const dx = aimPoint.x - this.#lastGroundCheck.x;
    const dz = aimPoint.z - this.#lastGroundCheck.z;
    const threshold = dist * 0.01; // 1% of distance to aim point
    // this.#groundY = 0;
    if (dx * dx + dz * dz > threshold * threshold) {
      if (!!this.scene) {
        const ground = GroundUtils.getGroundYFromScene(this.scene, aimPoint.x, aimPoint.z);
        if (ground) {
          this.#groundY = ground.y;
        }
      }
      this.#lastGroundCheck.set(aimPoint.x, 0, aimPoint.z);
    }
    aimPoint.y = THREE.MathUtils.lerp(aimPoint.y, this.#groundY, 0.3);

    this.setPositions(startPoint, aimPoint);
    return true;
  }

  track(dt: number, startPoint: THREE.Vector3, targetPosition: THREE.Vector3) {
    if (dt && this.isTracking) {
      const posSmooth  = 1 - Math.exp(-dt * 2.5);
      const lookSmooth = 1 - Math.exp(-dt * 3.5);
      const tmpBack = this.#tmpBack.copy(this.shotDirection).negate();

      this.staticCamPos.copy(startPoint);

      this.desiredCamPos.copy(targetPosition).addScaledVector(tmpBack, this.cameraTrackingOffsetYZ[1]);
      this.desiredCamPos.y += this.cameraTrackingOffsetYZ[0];
      this.desiredLookAt.copy(targetPosition);
      
      this.position.lerp(this.desiredCamPos, posSmooth);
      this.currentLookAt.lerp(this.desiredLookAt, lookSmooth);
      this.lookAt(this.currentLookAt);
      this.applyFrustumOffset(dt, 0, true);

      this.fov = this.cameraTrackingFov;
    }
  }

  update(dt: number, startPoint: THREE.Vector3, aimPoint: THREE.Vector3) {
    const aimChanged = !!this.updateAim(dt, startPoint, aimPoint);
    this.position.copy(this.staticCamPos);
    this.currentLookAt.copy(this.staticLookAt);
    this.lookAt(this.currentLookAt);
    this.applyFrustumOffset(dt, this.cameraOffsetX, false);

    
    // this.isAiming = aimChanged;

    if (aimChanged) return true;
    return false;
  }
}